import { useState, useRef, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { uploadAvatar } from '../../firebase/storage';
import { updateUserProfile } from '../../firebase/firestore';
import { Button, Input } from '../ui';
import { cn } from '../../lib/utils';
import { profileUpdateSchema } from '../../lib/validation';

/**
 * ProfileTab - Edit user profile information
 *
 * Allows editing:
 * - Display name
 * - Profile photo (avatar)
 * - Location/area
 */
export default function ProfileTab() {
  const { user, userProfile, updateName, authLoading } = useAuth();
  const { showToast } = useToast();

  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const fileInputRef = useRef(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm({
    resolver: zodResolver(profileUpdateSchema),
    defaultValues: {
      displayName: user?.displayName || '',
      location: userProfile?.location || '',
    },
  });

  // Reset form when user data loads
  useEffect(() => {
    if (user || userProfile) {
      reset({
        displayName: user?.displayName || '',
        location: userProfile?.location || '',
      });
    }
  }, [user, userProfile, reset]);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      showToast({ title: 'Invalid file', description: 'Please select an image file', type: 'error' });
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      showToast({ title: 'File too large', description: 'Maximum file size is 5MB', type: 'error' });
      return;
    }

    setUploading(true);
    try {
      const photoURL = await uploadAvatar(user.uid, file);
      await updateUserProfile(user.uid, { photoURL });
      showToast({ title: 'Photo updated', type: 'success' });
    } catch (error) {
      console.error('Error uploading photo:', error);
      showToast({ title: 'Upload failed', description: error.message, type: 'error' });
    } finally {
      setUploading(false);
    }
  };

  const onSubmit = async (data) => {
    setSaving(true);
    try {
      // Update display name in Auth
      if (data.displayName !== user?.displayName) {
        await updateName(data.displayName);
      }

      // Update location in Firestore
      if (data.location !== userProfile?.location) {
        await updateUserProfile(user.uid, { location: data.location });
      }

      showToast({ title: 'Profile updated', type: 'success' });
      reset(data); // Reset form with new values to clear isDirty
    } catch (error) {
      console.error('Error updating profile:', error);
      showToast({ title: 'Update failed', description: error.message, type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Profile Photo */}
      <div>
        <h3 className="text-lg font-semibold text-cream mb-4">Profile Photo</h3>
        <div className="flex items-center gap-6">
          <div className="relative">
            <div className="w-24 h-24 rounded-full bg-midnight-lighter border-2 border-cream/10 overflow-hidden">
              {userProfile?.photoURL ? (
                <img
                  src={userProfile.photoURL}
                  alt="Profile"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-cream/40">
                  <UserIcon className="w-12 h-12" />
                </div>
              )}
            </div>
            {uploading && (
              <div className="absolute inset-0 bg-midnight/70 rounded-full flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            )}
          </div>

          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handlePhotoUpload}
              className="hidden"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              loading={uploading}
            >
              {uploading ? 'Uploading...' : 'Change Photo'}
            </Button>
            <p className="text-xs text-cream/40 mt-2">
              JPG, PNG or GIF. Max 5MB.
            </p>
          </div>
        </div>
      </div>

      {/* Display Name */}
      <div>
        <h3 className="text-lg font-semibold text-cream mb-4">Personal Information</h3>
        <div className="space-y-4 max-w-md">
          <Input
            label="Display Name"
            placeholder="Your name"
            maxLength={50}
            error={errors.displayName?.message}
            {...register('displayName')}
          />

          <Input
            label="Location"
            placeholder="e.g., Dublin 8"
            helperText="Your general area (shown on competitions you create)"
            error={errors.location?.message}
            {...register('location')}
          />
        </div>
      </div>

      {/* Account Info (Read-only) */}
      <div>
        <h3 className="text-lg font-semibold text-cream mb-4">Account Information</h3>
        <div className="space-y-4 max-w-md">
          <div>
            <label className="block text-xs font-medium text-cream/60 uppercase tracking-wider mb-2">
              Email
            </label>
            <div className="flex items-center gap-2">
              <span className="text-cream">{user?.email}</span>
              {user?.emailVerified && (
                <span className="text-xs text-success flex items-center gap-1">
                  <CheckIcon className="w-3 h-3" /> Verified
                </span>
              )}
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-cream/60 uppercase tracking-wider mb-2">
              Phone
            </label>
            <div className="flex items-center gap-2">
              <span className="text-cream">
                {userProfile?.phone || 'Not set'}
              </span>
              {userProfile?.phoneVerified && (
                <span className="text-xs text-success flex items-center gap-1">
                  <CheckIcon className="w-3 h-3" /> Verified
                </span>
              )}
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-cream/60 uppercase tracking-wider mb-2">
              Member Since
            </label>
            <span className="text-cream">
              {userProfile?.createdAt
                ? new Date(userProfile.createdAt.seconds * 1000).toLocaleDateString('en-IE', {
                    month: 'long',
                    year: 'numeric',
                  })
                : 'Unknown'}
            </span>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="pt-4 border-t border-cream/10">
        <Button
          onClick={handleSubmit(onSubmit)}
          disabled={!isDirty || saving || authLoading}
          loading={saving}
        >
          Save Changes
        </Button>
      </div>
    </div>
  );
}

// Icons
function UserIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
  );
}

function CheckIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  );
}
